import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import type { Product } from '@techpos/contracts';
import { apiClient } from './api/client.js';

export type ProductChoice = Pick<Product, 'id' | 'name' | 'productCode'>;
const display = (product: ProductChoice): string => `${product.name}${product.productCode ? ` · ${product.productCode}` : ''}`;
const PAGE_SIZE = 20;

/** Uses catalogue search, never loads or renders the full product catalogue. */
export function ProductSearch({ value, onChange, supplierId, disabled = false }: {
  readonly value: ProductChoice | null;
  readonly onChange: (product: Product | null) => void;
  readonly supplierId?: string;
  readonly disabled?: boolean;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const generation = useRef(0);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<Product[]>([]);
  const [active, setActive] = useState(-1);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [more, setMore] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => { input.current?.setCustomValidity(value ? '' : 'Choose a product from the search results.'); }, [value]);
  useEffect(() => {
    const request = ++generation.current;
    let cancelled = false;
    const search = query.trim();
    setResults([]); setActive(-1); setMore(false);
    if (disabled || !open || value || !search) return;
    setStatus('loading');
    const timer = setTimeout(() => {
      void apiClient.products({ search, page: 1, pageSize: PAGE_SIZE, status: 'ACTIVE', ...(supplierId ? { supplierId, activeSupplierOnly: true } : {}) })
        .then(response => {
          if (cancelled || generation.current !== request) return;
          // Draft editing retains its existing active supplier-relationship restriction.
          const matches = response.items.filter(product => !supplierId || product.suppliers.some(relation => relation.supplierId === supplierId && relation.active));
          setResults(matches.slice(0, PAGE_SIZE)); setMore(response.total > response.items.length); setStatus('ready');
        }).catch(() => { if (!cancelled && generation.current === request) { setResults([]); setStatus('error'); } });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, open, supplierId, value, retry, disabled]);

  const resetSearch = (text: string) => {
    generation.current++; setQuery(text); setResults([]); setActive(-1); setMore(false); setStatus(text.trim() ? 'loading' : 'idle');
  };
  const choose = (product: Product) => {
    resetSearch(''); setOpen(false); onChange(product); input.current?.focus();
  };
  const keys = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); setOpen(false); return; }
    if (event.key === 'Enter' && !value) {
      event.preventDefault(); if (open && results[active]) choose(results[active]); return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault(); setOpen(true);
    if (!results.length) return;
    const next = event.key === 'ArrowDown' ? (active + 1) % results.length : (active <= 0 ? results.length - 1 : active - 1);
    setActive(next); document.getElementById(`${id}-option-${next}`)?.scrollIntoView?.({ block: 'nearest' });
  };
  const expanded = !disabled && open && !value;
  return <div className="po-product-search" onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
    <label htmlFor={id}>Product</label>
    <div className="po-product-input"><input disabled={disabled} ref={input} id={id} role="combobox" aria-autocomplete="list" aria-expanded={expanded}
      aria-controls={`${id}-results`} aria-activedescendant={expanded && active >= 0 ? `${id}-option-${active}` : undefined}
      aria-describedby={`${id}-hint`} autoComplete="off" maxLength={160} required placeholder="Search products…"
      value={value ? display(value) : query} onFocus={event => { setOpen(true); if (value) event.currentTarget.select(); }}
      onChange={event => { resetSearch(event.target.value); setOpen(true); if (value) onChange(null); }} onKeyDown={keys} />
      {(value || query) && <button type="button" aria-label="Clear product" onClick={() => { resetSearch(''); onChange(null); setOpen(true); input.current?.focus(); }}>Clear</button>}
    </div>
    <small id={`${id}-hint`}>{disabled ? 'Select a supplier first.' : value ? 'Selected: ' + display(value) + '. Clear or type to change.' : 'Search name, stock code, barcode or supplier code.'}</small>
    {expanded && <div className="po-product-results">
      <p role="status">{!query.trim() ? 'Type to search active products.' : status === 'loading' ? 'Searching…' : status === 'error' ? 'Product search failed. Please retry.' : results.length ? `${results.length} results${more ? ' · Type more to narrow the search.' : ''}` : supplierId ? 'No eligible products. Try another name/code, choose another supplier, or review the product’s supplier relationships in Products.' : 'No matching products. Try another name or code.'}</p>
      {status === 'error' && <button type="button" onClick={() => { setStatus('loading'); setRetry(count => count + 1); }}>Retry search</button>}
      <ul id={`${id}-results`} role="listbox" aria-label="Product search results">
        {results.map((product, index) => <li key={product.id} role="presentation"><button type="button" role="option" id={`${id}-option-${index}`}
          aria-selected={index === active} tabIndex={-1} onPointerDown={event => event.preventDefault()} onClick={() => choose(product)}>
          <strong>{product.name}</strong><small>Stock code: {product.productCode ?? '—'} · Barcode: {product.barcodes.find(barcode => barcode.isPrimary)?.barcode ?? product.barcodes[0]?.barcode ?? '—'}</small>
          {product.suppliers.some(relation => relation.supplierProductCode) && <small>Supplier codes: {product.suppliers.map(relation => relation.supplierProductCode).filter(Boolean).join(', ')}</small>}
        </button></li>)}
      </ul>
    </div>}
  </div>;
}

